// India hubs — /online-tuition-india-states and /online-tuition-india-cities.
// Hosting these as dedicated React pages (not authority-page configs) because
// they need to render dynamic grids from V7_STATES_LIST / V7_TIER2_CITY_LIST
// plus existing core/metro city links. Authority pages assume a fixed pillar+FAQ
// shape — these are catalogue pages.

import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { V7_STATES_LIST, V7_TIER2_CITY_LIST } from "../data/indiaProgrammaticPages";

const CORE_CITIES = [
    { name: "Jabalpur", path: "/jabalpur" },
    { name: "Bhopal", path: "/bhopal" },
    { name: "Nagpur", path: "/nagpur" },
    { name: "Mumbai", path: "/online-tuition-mumbai" },
    { name: "Delhi NCR", path: "/online-tuition-delhi-ncr" },
    { name: "Bangalore", path: "/online-tuition-bangalore" },
    { name: "Pune", path: "/online-tuition-pune" },
    { name: "Hyderabad", path: "/online-tuition-hyderabad" },
    { name: "Chennai", path: "/online-tuition-chennai" },
    { name: "Kolkata", path: "/online-tuition-kolkata" },
    { name: "Ahmedabad", path: "/online-tuition-ahmedabad" },
    { name: "Indore", path: "/online-tuition-indore" },
];


export function StatesHubPage() {
    return (
        <div className="bg-white">
            <SEO
                title="Online Tuition in India — All 28 States & UTs Covered | Anand Tutorials"
                description="Anand Tutorials' 1:1 live online tuition is active across all 28 Indian states and 8 Union Territories. Find your state's dedicated online tuition page with city interlinks. Free demo, no card."
                keywords="online tuition india states, online tuition all states, online tuition uttar pradesh, online tuition maharashtra, online tuition karnataka, online tuition tamil nadu"
                path="/online-tuition-india-states"
            />
            <PageHeader
                eyebrow="🇮🇳 Pan-India Coverage"
                title="1:1 Online Tuition — Every Indian State & Union Territory"
                subtitle="Find your state's dedicated page. We're active everywhere in India — from major cities to smaller towns and pin-code areas."
            />
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
                <Breadcrumbs items={[{ name: "Online Classes", to: "/online-classes" }, { name: "All States" }]} />
            </div>
            <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {V7_STATES_LIST.map((s) => (
                        <Link
                            key={s.slug}
                            to={s.path}
                            data-testid={`state-link-${s.slug}`}
                            className="group bg-[#F8FAFC] border border-slate-200 hover:border-blue-400 hover:bg-white hover:shadow-md transition-all rounded-xl p-5"
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-[#1E3A8A]" />
                                    <h3 className="font-heading font-semibold text-[#0F172A] group-hover:text-[#1E3A8A]">{s.name}</h3>
                                </div>
                                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-transform" />
                            </div>
                            <p className="text-xs text-slate-500 mb-3">{s.cities.length} cities · {s.cities.slice(0, 3).join(", ")}{s.cities.length > 3 ? "…" : ""}</p>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}


export function CitiesHubPage() {
    return (
        <div className="bg-white">
            <SEO
                title="Online Tuition Across India — All Cities Covered | Anand Tutorials"
                description="Anand Tutorials' 1:1 live online tuition is active in 40+ Indian cities — from Tier-1 metros (Mumbai, Delhi, Bangalore) to Tier-2 / Tier-3 (Lucknow, Jaipur, Patna, Surat). Find your city's dedicated online tuition page."
                keywords="online tuition india cities, online tuition all cities, online tutor every city, online tuition tier 2 city, online tuition tier 3 city, online tuition near me india"
                path="/online-tuition-india-cities"
            />
            <PageHeader
                eyebrow="🇮🇳 Pan-India Coverage"
                title="1:1 Online Tuition — Every Major Indian City"
                subtitle="From Tier-1 metros to Tier-2 / Tier-3 cities. The tutor lives anywhere in India; the class is in your child's room."
            />
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
                <Breadcrumbs items={[{ name: "Online Classes", to: "/online-classes" }, { name: "All Cities" }]} />
            </div>
            <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
                <h2 className="font-heading text-2xl font-bold mb-5 text-[#0F172A]">Core & Tier-1 Metros</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
                    {CORE_CITIES.map((c) => (
                        <Link key={c.path} to={c.path} className="group bg-[#F8FAFC] border border-slate-200 hover:border-blue-400 hover:bg-white transition-all rounded-lg px-4 py-3 flex items-center justify-between">
                            <span className="text-slate-700 group-hover:text-[#1E3A8A]">{c.name}</span>
                            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    ))}
                </div>
                <h2 className="font-heading text-2xl font-bold mb-5 text-[#0F172A]">Tier-2 & Tier-3 Cities</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {V7_TIER2_CITY_LIST.map((c) => (
                        <Link key={c.slug} to={c.path} data-testid={`tier2-link-${c.slug}`} className="group bg-[#F8FAFC] border border-slate-200 hover:border-blue-400 hover:bg-white transition-all rounded-lg px-4 py-3 flex items-center justify-between">
                            <div>
                                <div className="text-slate-700 group-hover:text-[#1E3A8A]">{c.name}</div>
                                <div className="text-xs text-slate-500">{c.state}</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
